import React from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Form } from '@openedx/paragon';

import {
  EnrolledUser,
  getEnrolledUsers,
  MasqueradeStatus,
  Payload,
} from './data/api';
import messages from './messages';

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onSubmit' | 'onError'> {
  courseId: string;
  onError: (error: string) => void;
  onSubmit: (payload: Payload) => Promise<MasqueradeStatus>;
}

export const MasqueradeUserNameInput: React.FC<Props> = ({
  className = '',
  courseId,
  defaultValue,
  onFocus,
  onSubmit,
  onError,
  ...otherProps
}) => {
  const intl = useIntl();
  const [isFocused, setIsFocused] = React.useState(false);
  const [isSearchPending, setIsSearchPending] = React.useState(false);
  const [userIdentifier, setUserIdentifier] = React.useState(String(defaultValue || ''));
  const [users, setUsers] = React.useState<EnrolledUser[]>([]);
  const searchTimeoutId = React.useRef<number | null>(null);

  const handleSubmit = React.useCallback((userIdentifier: string) => {
    const payload: Payload = {
      role: 'student',
      user_name: userIdentifier, // user name or email
    };
    onSubmit(payload).then((data) => {
      if (data && data.success) {
        global.location.reload();
      } else {
        const error = (data && data.error) || '';
        onError(error);
      }
    }).catch(() => {
      const message = intl.formatMessage(messages.genericError);
      onError(message);
    });
    return true;
  }, [intl, onError, onSubmit]);

  React.useEffect(() => {
    setUserIdentifier(String(defaultValue || ''));
  }, [defaultValue]);

  React.useEffect(() => {
    if (searchTimeoutId.current) {
      window.clearTimeout(searchTimeoutId.current);
      searchTimeoutId.current = null;
    }

    if (!isFocused) {
      setIsSearchPending(false);
      return undefined;
    }

    const query = userIdentifier.trim();
    let isCurrent = true;
    setIsSearchPending(true);
    searchTimeoutId.current = window.setTimeout(() => {
      getEnrolledUsers(courseId, query)
        .then((nextUsers) => {
          if (isCurrent) {
            setUsers(nextUsers);
          }
        })
        .catch(() => {
          if (isCurrent) {
            setUsers([]);
          }
        })
        .finally(() => {
          if (isCurrent) {
            setIsSearchPending(false);
          }
        });
    }, query ? 250 : 0);

    return () => {
      isCurrent = false;
      if (searchTimeoutId.current) {
        window.clearTimeout(searchTimeoutId.current);
        searchTimeoutId.current = null;
      }
    };
  }, [courseId, isFocused, userIdentifier]);

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUserIdentifier(event.target.value);
  }, []);

  const handleFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  }, [onFocus]);

  const handleBlur = React.useCallback(() => {
    window.setTimeout(() => {
      setIsFocused(false);
    }, 150);
  }, []);

  const handleKeyPress = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      return handleSubmit(event.currentTarget.value);
    }
    return true;
  }, [handleSubmit]);

  const handleSelectUser = React.useCallback((user: EnrolledUser) => {
    setUserIdentifier(user.username);
    handleSubmit(user.username);
  }, [handleSubmit]);

  const isSearching = Boolean(userIdentifier.trim());
  const visibleUsers = isSearching ? users : users.slice(0, 5);

  return (
    <div className={`${className} position-relative`}>
      <Form.Control
        aria-labelledby="masquerade-search-label"
        autoComplete="off"
        label={intl.formatMessage(messages.userNameLabel)}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyPress={handleKeyPress}
        type="text"
        value={userIdentifier}
        {...otherProps}
      />
      {isFocused && (
        <div
          className="dropdown-menu show w-100 mt-1"
          role="listbox"
          style={{ maxHeight: '16rem', overflowY: 'auto', zIndex: 1030 }}
        >
          {visibleUsers.map((user) => (
            <button
              aria-selected="false"
              className="dropdown-item text-wrap"
              key={user.id}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelectUser(user);
              }}
              role="option"
              type="button"
            >
              <span className="d-block">{user.username}</span>
              <small className="text-muted">{user.email}</small>
            </button>
          ))}
          {!visibleUsers.length && !isSearchPending && (
            <div className="dropdown-item-text text-muted">
              {intl.formatMessage(messages.noEnrolledUsersFound)}
            </div>
          )}
          {isSearchPending && (
            <div className="dropdown-item-text text-muted">
              {intl.formatMessage(messages.searchingUsers)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
